import { useState } from 'react';

import { Modal } from '@/ui/Modal';
import { ParticipantsPicker } from './ParticipantsPicker';
import { useParticipantsPickerState } from './useParticipantsPickerState';
import { NoUsersModalContent } from './NoUsersModalContent';
import { ModalProps } from '@/ui/Modal/types';

type AddParticipantsModalProps = {
  open: boolean;
  alreadyAddedUserIds?: number[];
  onClose: () => void;
  onSave: (selectedUserIds: number[]) => void;
};

export const AddParticipantsModal = ({
  open,
  alreadyAddedUserIds = [],
  onClose,
  onSave,
}: AddParticipantsModalProps): JSX.Element => {
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const { onLoadMore, loading, usersList, usersTotal, canShowMore, onSearchPhraseChange, searchPhrase } =
    useParticipantsPickerState();

  const stagedUserIds = selectedUserIds.filter((id) => !alreadyAddedUserIds.includes(id));

  // The modal stays mounted while closed, so staged state has to be discarded explicitly.
  const handleClose = () => {
    setSelectedUserIds([]);
    onSearchPhraseChange('');
    onClose();
  };

  const noUsersExist = !loading && !usersTotal && !searchPhrase;

  const modalProps: Omit<ModalProps, 'children'> = { open, onClose: handleClose, size: 'mobileLandscape' };

  if (noUsersExist)
    return (
      <Modal {...modalProps}>
        <NoUsersModalContent onClose={handleClose} />
      </Modal>
    );

  return (
    <Modal {...modalProps}>
      <Modal.Title>Add learners</Modal.Title>

      <Modal.Contents>
        <ParticipantsPicker
          selectedUserIds={selectedUserIds}
          setSelectedUserIds={setSelectedUserIds}
          alreadyAddedUserIds={alreadyAddedUserIds}
          loading={loading}
          searchPhrase={searchPhrase}
          onSearchPhraseChange={onSearchPhraseChange}
          canShowMore={canShowMore}
          usersList={usersList}
          onLoadMore={onLoadMore}
        />
      </Modal.Contents>
      <Modal.Actions>
        {<Modal.Action action={handleClose}>Cancel</Modal.Action>}
        <Modal.Action
          action={() => {
            onSave(stagedUserIds);
            handleClose();
          }}
          disabled={!stagedUserIds.length}
          primary
        >
          Add
        </Modal.Action>
      </Modal.Actions>
    </Modal>
  );
};
